/**
 * O que o Kanban lembra de uma visita para a outra (SPEC-20260921-2304).
 *
 * Voltar a um quadro e encontrar tudo reaberto, no topo, obriga a refazer o
 * mesmo trabalho de arrumação toda vez — e em quadro grande isso é caro.
 *
 * Fica no navegador, ao lado do agrupamento escolhido (`orbind.kb.groupby.*`)
 * e da altura do cabeçalho (`orbind.kb.headh.*`), que já moram lá desde
 * 2026-08-27. É preferência de quem trabalha, não dado do quadro: não vai para
 * o banco e não vale para os colegas.
 *
 * Toda leitura devolve um padrão utilizável quando não há nada guardado, o
 * conteúdo está corrompido ou o armazenamento nem existe (janela privativa).
 */

/**
 * As bandas recolhidas são por quadro E POR AGRUPAMENTO.
 *
 * A chave de uma banda é o valor do atributo agrupado — o id de uma área, de
 * uma pessoa, um código de tipo. Recolher "Vendas" agrupando por área não diz
 * nada sobre o mesmo quadro agrupado por pessoa, e guardar os dois juntos
 * reabriria banda errada na troca.
 */
export function chaveDasBandas(quadroId: string, agruparPor: string): string {
  return `orbind.kb.fechadas.${quadroId}.${agruparPor}`
}

export function lerBandasFechadas(quadroId: string, agruparPor: string): Set<string> {
  try {
    const cru = localStorage.getItem(chaveDasBandas(quadroId, agruparPor))
    if (!cru) return new Set()
    const lido: unknown = JSON.parse(cru)
    if (!Array.isArray(lido)) return new Set()
    return new Set(lido.filter((x): x is string => typeof x === 'string'))
  } catch {
    return new Set()
  }
}

export function guardarBandasFechadas(quadroId: string, agruparPor: string, chaves: Set<string>): void {
  try {
    const chave = chaveDasBandas(quadroId, agruparPor)
    // nada recolhido é o padrão: não vale ocupar espaço guardando "[]"
    if (chaves.size === 0) localStorage.removeItem(chave)
    else localStorage.setItem(chave, JSON.stringify([...chaves]))
  } catch {
    /* sem storage: o quadro funciona igual, só não lembra */
  }
}

/* ------------------------------------------------ onde a pessoa parou --- */

export type Rolagem = { top: number; left: number }

/**
 * A rolagem sobrevive a FECHAR o aplicativo (decisão do usuário, 2026-09-21).
 *
 * Antes vivia em `sessionStorage`, que morre junto com a aba: voltar no dia
 * seguinte começava do topo. O pedido foi explícito — "a posição de onde
 * estava no scroll, quando saiu" — então passou para `localStorage`.
 *
 * Quadro e planilha guardam separado: a planilha rola na vertical por linhas e
 * o quadro rola na horizontal por colunas; um ponto não serve para o outro.
 */
export function lerRolagem(chave: string): Rolagem | null {
  try {
    const cru = localStorage.getItem(chave)
    if (!cru) return null
    const lido = JSON.parse(cru) as Partial<Rolagem>
    const numero = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0)
    return { top: numero(lido.top), left: numero(lido.left) }
  } catch {
    return null
  }
}

export function guardarRolagem(chave: string, posicao: Rolagem): void {
  try {
    // topo absoluto é o padrão: não guarda nada e evita lixo por quadro visitado
    if (posicao.top === 0 && posicao.left === 0) localStorage.removeItem(chave)
    else localStorage.setItem(chave, JSON.stringify({ top: posicao.top, left: posicao.left }))
  } catch {
    /* sem storage: volta ao topo, como era antes */
  }
}

/* ------------------------------------------- recolher tudo de uma vez --- */

/**
 * O próximo conjunto de recolhidos ao acionar o "recolher/expandir tudo" de um
 * grupo (SPEC-20260922-2005).
 *
 * Só EXPANDE quando todos os filhos já estão recolhidos. Com a maioria aberta,
 * o gesto esperado é fechar o que sobrou — não reabrir o que a pessoa acabou
 * de fechar. Tratar "algum aberto" como expandir faria o botão desfazer o
 * próprio trabalho de quem clicou duas vezes seguidas.
 */
export function alternarTodosOsFilhos(fechadas: Set<string>, filhos: readonly string[]): Set<string> {
  if (filhos.length === 0) return fechadas
  const todosFechados = filhos.every((chave) => fechadas.has(chave))
  const proximo = new Set(fechadas)
  for (const chave of filhos) {
    if (todosFechados) proximo.delete(chave)
    else proximo.add(chave)
  }
  return proximo
}
